// LimpackFlex (admin · Logística) — rentabilidad del envío en el día + palanca real.
//
// Todo el envío OneShot/Flex en un solo lugar:
//   1) Calculadora de rentabilidad en vivo (lee zonas_envio + envio_tarifas + margen real de productos).
//   2) Editor de zonas (costo_nahuel / precio_flex / umbral_gratis) — superadmin.
//   3) Editor de parámetros OneShot (umbral gratis MVD, privado precio/costo, interior fijo, cortes).
//
// La calculadora refleja los valores EN EDICIÓN, así ves el impacto en margen antes de guardar.
// RBAC: superadmin edita (RLS is_superadmin en ambas tablas). Admin ve read-only (useCanEdit + SoloLecturaBanner).
// Fuente de verdad del cobro real al cliente: RPC calcular_envio_oneshot (backend). Esto es gestión + preview.

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import AdminLayout from '../../components/AdminLayout'
import SoloLecturaBanner from '../../components/SoloLecturaBanner'
import { useCanEdit } from '../../lib/useCanEdit'

const IVA = 1.22
const fmt = (n) => (n < 0 ? '−' : '') + '$' + Math.round(Math.abs(Number(n) || 0)).toLocaleString('es-UY')
const pct = (n) => (Number(n) || 0).toFixed(1).replace('.', ',') + '%'

// Parámetros OneShot que viven en envio_tarifas (clave → monto).
const PARAM_META = [
  { clave: 'oneshot_umbral_gratis',              titulo: 'Umbral envío gratis · Montevideo', desc: 'Monto mínimo del pedido para que el Flex de MVD salga gratis.', unidad: '$', color: '#1A3461' },
  { clave: 'oneshot_privado_canelones_precio',   titulo: 'Privado Canelones · precio',        desc: 'Lo que le cobrás al cliente por barrio privado/cerrado.',        unidad: '$', color: '#6B7280' },
  { clave: 'oneshot_privado_canelones_costo',    titulo: 'Privado Canelones · costo Nahuel',  desc: 'Lo que te cuesta el cadete en privados. Margen del envío = precio − costo.', unidad: '$', color: '#EF4444' },
  { clave: 'oneshot_interior_fijo',              titulo: 'Interior · fijo despacho',          desc: 'Fijo por llevar el paquete a Terminal Tres Cruces (gratis ≥ umbral). La encomienda la paga el cliente en destino con CI.', unidad: '$', color: '#F59E0B' },
  { clave: 'oneshot_corte_flex_hora',            titulo: 'Corte Flex · hora',                 desc: 'Hora de corte para que el Flex llegue el mismo día (lun–vie).', unidad: 'h', color: '#00A8B5' },
  { clave: 'oneshot_corte_interior_hora',        titulo: 'Corte interior · hora',             desc: 'Hora de corte para despachar a Tres Cruces en el día.',          unidad: 'h', color: '#F59E0B' },
]
const PARAM_CLAVES = PARAM_META.map((p) => p.clave)

// ─── Mapa de zonas (geojson barrios de Montevideo, open data) ───
const GEO_URLS = [
  'https://cdn.jsdelivr.net/gh/vierja/geojson_montevideo@master/barrios.geojson',
  'https://raw.githubusercontent.com/vierja/geojson_montevideo/master/barrios.geojson',
]
const ZCOLOR = { verde: '#22C55E', naranja: '#F59E0B', rojo: '#EF4444', extendida: '#6B7280', none: '#CBD5E1' }
const ZLABEL = { verde: 'Verde · MVD cercano', naranja: 'Naranja · MVD medio', rojo: 'Roja · MVD lejano', extendida: 'Extendida · Canelones' }
const ZONA_OPCIONES = ['verde', 'naranja', 'rojo', 'extendida']
const norm = (s) => (s || '').toString().toUpperCase().normalize('NFD').replace(/\p{Diacritic}/gu, '').replace(/[^A-Z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim()
function ringsDe(geom) {
  if (!geom) return []
  if (geom.type === 'Polygon') return [geom.coordinates[0]]
  if (geom.type === 'MultiPolygon') return geom.coordinates.map((p) => p[0])
  return []
}

export default function LimpackFlex() {
  const canEdit = useCanEdit()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)

  const [zonas, setZonas] = useState([])
  const [zonasOrig, setZonasOrig] = useState([])
  const [params, setParams] = useState({})
  const [paramsOrig, setParamsOrig] = useState({})
  const [margenReal, setMargenReal] = useState(52.9)
  const [nProductos, setNProductos] = useState(0)

  // Controles de la calculadora
  const [sel, setSel] = useState('rojo')
  const [order, setOrder] = useState(1000)
  const [marginPct, setMarginPct] = useState(52.9)
  const [ivaMode, setIvaMode] = useState('con')

  // Mapa de zonas
  const [geo, setGeo] = useState(null)
  const [geoStatus, setGeoStatus] = useState('loading')
  const [zbg, setZbg] = useState({})          // norm(nombre) -> { nombre, zona }
  const [hover, setHover] = useState(null)     // { nombre, zona, x, y }
  const [picker, setPicker] = useState(null)   // { key, nombre, x, y }
  const [savingZona, setSavingZona] = useState(false)

  useEffect(() => { cargar() }, [])

  // Baja el geojson de barrios de Montevideo (open data) en el navegador del usuario.
  useEffect(() => {
    let cancel = false
    ;(async () => {
      setGeoStatus('loading')
      for (const u of GEO_URLS) {
        try {
          const res = await fetch(u)
          if (res.ok) { const j = await res.json(); if (!cancel) { setGeo(j); setGeoStatus('ok') } return }
        } catch (e) { /* probar siguiente url */ }
      }
      if (!cancel) setGeoStatus('error')
    })()
    return () => { cancel = true }
  }, [])

  async function cargar() {
    setLoading(true); setMsg(null)
    try {
      const [{ data: zData, error: zErr }, { data: tData, error: tErr }, { data: pData, error: pErr }, { data: gData }] = await Promise.all([
        supabase.from('zonas_envio').select('id, codigo, nombre, orden, costo_nahuel, precio_flex, umbral_gratis, activo, color_hex').order('orden'),
        supabase.from('envio_tarifas').select('clave, monto').in('clave', PARAM_CLAVES),
        supabase.from('productos').select('precio_publico, costo_unitario').eq('activo', true).gt('precio_publico', 0).gt('costo_unitario', 0),
        supabase.from('zona_barrio_geo').select('nombre, zona_codigo'),
      ])
      if (zErr) throw zErr
      if (tErr) throw tErr
      if (pErr) throw pErr

      const z = (zData || []).map((r) => ({
        ...r,
        costo_nahuel: Number(r.costo_nahuel) || 0,
        precio_flex: Number(r.precio_flex) || 0,
        umbral_gratis: r.umbral_gratis == null ? null : Number(r.umbral_gratis),
      }))
      setZonas(z)
      setZonasOrig(JSON.parse(JSON.stringify(z)))

      const pmap = {}
      for (const c of PARAM_CLAVES) pmap[c] = 0
      for (const row of tData || []) pmap[row.clave] = Number(row.monto)
      setParams(pmap)
      setParamsOrig({ ...pmap })

      const prods = pData || []
      if (prods.length) {
        const m = prods.reduce((a, p) => a + (Number(p.precio_publico) - Number(p.costo_unitario)) / Number(p.precio_publico), 0) / prods.length
        const mPct = Math.round(m * 1000) / 10
        setMargenReal(mPct)
        setMarginPct(mPct)
        setNProductos(prods.length)
      }

      const gmap = {}
      for (const row of gData || []) gmap[norm(row.nombre)] = { nombre: row.nombre, zona: row.zona_codigo }
      setZbg(gmap)
    } catch (e) {
      setMsg({ ok: false, txt: 'Error cargando datos: ' + (e.message || e) })
    } finally {
      setLoading(false)
    }
  }

  // ─── Zonas para la calculadora (incluye "privado" armado desde params) ───
  const calcZonas = useMemo(() => {
    const umbralMVD = params.oneshot_umbral_gratis || 1000
    const extendida = zonas.find((z) => z.codigo === 'extendida')
    const umbralExt = extendida?.umbral_gratis || 2000
    const base = zonas.map((z) => ({
      code: z.codigo,
      nm: z.nombre,
      cost: z.costo_nahuel,
      charge: z.precio_flex,
      thr: z.umbral_gratis != null ? z.umbral_gratis : umbralMVD,
    }))
    base.push({
      code: 'privado',
      nm: 'Privados Canelones',
      cost: params.oneshot_privado_canelones_costo || 0,
      charge: params.oneshot_privado_canelones_precio || 0,
      thr: umbralExt,
    })
    return base
  }, [zonas, params])

  function calc(z, ord, mPct, iva) {
    const m = mPct / 100
    const free = ord >= z.thr
    const div = iva === 'neto' ? IVA : 1
    const gp = (ord * m) / div
    const shipRev = (free ? 0 : z.charge) / div
    const shipCost = z.cost
    const contrib = gp + shipRev - shipCost
    const be = m > 0 ? z.cost / m : 0
    const flForGP = free && gp > 0 ? (shipCost / gp) * 100 : 0
    return { free, gp, shipRev, shipCost, contrib, be, flForGP }
  }

  const zSel = calcZonas.find((z) => z.code === sel) || calcZonas[0]
  const r = zSel ? calc(zSel, order, marginPct, ivaMode) : null

  function verdict(rr) {
    if (!rr) return { cls: 'v-ok', t: '—', s: '' }
    if (rr.contrib <= 0) return { cls: 'v-bad', t: 'En rojo', s: 'Este pedido pierde plata. Revisá umbral o costo.' }
    if (rr.free && rr.flForGP > 40) return { cls: 'v-warn', t: 'Sano pero fino', s: 'El flete se come más del 40% del bruto (canasta de margen bajo justo en el umbral).' }
    return { cls: 'v-ok', t: 'Rentable', s: rr.free ? 'El envío gratis está bien cubierto por el margen del producto.' : 'Pedido bajo el umbral: el cliente paga el envío.' }
  }
  const v = verdict(r)

  // ─── Mapa: detecta la propiedad del nombre, proyecta y colorea por zona ───
  const mapa = useMemo(() => {
    if (!geo || !geo.features) return null
    const feats = geo.features.filter((f) => f.geometry)
    if (!feats.length) return null
    const keys = Object.keys(feats[0].properties || {})
    let nameKey = keys[0], best = -1
    for (const k of keys) {
      let s = 0
      for (const f of feats) if (zbg[norm(f.properties[k])]) s++
      if (s > best) { best = s; nameKey = k }
    }
    let minLng = 1e9, maxLng = -1e9, minLat = 1e9, maxLat = -1e9
    feats.forEach((f) => ringsDe(f.geometry).forEach((rg) => rg.forEach(([lng, lat]) => {
      if (lng < minLng) minLng = lng
      if (lng > maxLng) maxLng = lng
      if (lat < minLat) minLat = lat
      if (lat > maxLat) maxLat = lat
    })))
    const midLat = (minLat + maxLat) / 2, kx = Math.cos((midLat * Math.PI) / 180)
    const W = 660, dLng = (maxLng - minLng) * kx || 1, dLat = (maxLat - minLat) || 1
    const H = Math.round((W * dLat) / dLng)
    const X = (lng) => (((lng - minLng) * kx) / dLng) * W
    const Y = (lat) => ((maxLat - lat) / dLat) * H
    const dPath = (rg) => 'M' + rg.map(([lng, lat]) => X(lng).toFixed(1) + ',' + Y(lat).toFixed(1)).join('L') + 'Z'
    let matched = 0
    const unmatched = []
    const counts = { verde: 0, naranja: 0, rojo: 0, extendida: 0, none: 0 }
    const paths = feats.map((f, i) => {
      const nombre = f.properties[nameKey]
      const hit = zbg[norm(nombre)]
      const zona = hit ? hit.zona : 'none'
      if (hit) matched++; else unmatched.push(nombre)
      counts[zona] = (counts[zona] || 0) + 1
      return { i, nombre, zona, d: ringsDe(f.geometry).map(dPath).join(' ') }
    })
    return { paths, W, H, matched, total: feats.length, unmatched, counts, nameKey }
  }, [geo, zbg])

  async function reasignarZona(nombre, zona) {
    if (!canEdit) return
    const key = norm(nombre)
    const nombreDB = zbg[key]?.nombre || key
    setSavingZona(true); setMsg(null)
    try {
      const { error } = await supabase.from('zona_barrio_geo').upsert({
        nombre: nombreDB, zona_codigo: zona, es_privado: false, updated_at: new Date().toISOString(),
      }, { onConflict: 'nombre' })
      if (error) throw error
      setZbg((prev) => ({ ...prev, [key]: { nombre: nombreDB, zona } }))
      setPicker(null)
    } catch (e) {
      setMsg({ ok: false, txt: 'Error al reasignar zona: ' + (e.message || e) })
    } finally {
      setSavingZona(false)
    }
  }

  // ─── Edición ───
  function setZona(id, campo, valor) {
    setZonas((prev) => prev.map((z) => (z.id === id ? { ...z, [campo]: valor } : z)))
  }
  function setParam(clave, valor) {
    setParams((prev) => ({ ...prev, [clave]: valor }))
  }
  const zonaDirty = (z) => {
    const o = zonasOrig.find((x) => x.id === z.id)
    return o && (z.costo_nahuel !== o.costo_nahuel || z.precio_flex !== o.precio_flex || z.umbral_gratis !== o.umbral_gratis)
  }
  const zonasDirty = zonas.filter(zonaDirty)
  const paramsDirty = PARAM_CLAVES.filter((c) => Number(params[c]) !== Number(paramsOrig[c]))
  const hayCambios = zonasDirty.length > 0 || paramsDirty.length > 0

  async function guardar() {
    if (!canEdit || !hayCambios) return
    setSaving(true); setMsg(null)
    try {
      for (const z of zonasDirty) {
        const { error } = await supabase.from('zonas_envio').update({
          costo_nahuel: z.costo_nahuel,
          precio_flex: z.precio_flex,
          umbral_gratis: z.umbral_gratis,
          updated_at: new Date().toISOString(),
        }).eq('id', z.id)
        if (error) throw error
      }
      for (const c of paramsDirty) {
        const { error } = await supabase.from('envio_tarifas').upsert({
          clave: c,
          monto: Number(params[c]) || 0,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'clave' })
        if (error) throw error
      }
      setZonasOrig(JSON.parse(JSON.stringify(zonas)))
      setParamsOrig({ ...params })
      setMsg({ ok: true, txt: '✓ Guardado. Aplica de inmediato a los próximos cálculos de envío.' })
    } catch (e) {
      setMsg({ ok: false, txt: 'Error al guardar: ' + (e.message || e) })
    } finally {
      setSaving(false)
    }
  }
  function descartar() {
    setZonas(JSON.parse(JSON.stringify(zonasOrig)))
    setParams({ ...paramsOrig })
    setMsg(null)
  }

  return (
    <AdminLayout>
      <div className="page lf-admin">
        <Link to="/admin/entregas" style={{ fontSize: 13, color: 'var(--muted)' }}>← Volver a logística</Link>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
          <div>
            <h1 style={{ margin: 0 }}>⚡ LimpackFlex · Envío en el día <SoloLecturaBanner /></h1>
            <p className="lead" style={{ margin: '4px 0 0', maxWidth: 760 }}>
              Rentabilidad del envío OneShot y la palanca de costos/tarifas por zona. La calculadora refleja los valores en edición: cambiá un costo y mirá el impacto en margen antes de guardar.
            </p>
          </div>
        </div>

        {msg && (
          <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 8, background: msg.ok ? '#DCFCE7' : '#FEE2E2', color: msg.ok ? '#166534' : '#7F1D1D', fontSize: 13, fontWeight: 600 }}>{msg.txt}</div>
        )}

        {loading ? (
          <div className="card" style={{ marginTop: 18, color: 'var(--muted)' }}>Cargando…</div>
        ) : (
          <>
            {/* ───────── CALCULADORA ───────── */}
            <div className="lf-calc" style={{ marginTop: 18 }}>
              <div className="lf-controls">
                <div className="lf-field">
                  <label>Zona</label>
                  <div className="lf-zbtns">
                    {calcZonas.map((z) => (
                      <button key={z.code} className={'lf-zbtn' + (z.code === sel ? ' on' : '')} onClick={() => setSel(z.code)}>
                        <b>{z.nm}</b><span>cuesta {fmt(z.cost)} · gratis desde {fmt(z.thr)}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="lf-field">
                  <label>Monto del pedido <b>{fmt(order)}</b></label>
                  <input type="range" min="200" max="4000" step="50" value={order} onChange={(e) => setOrder(Number(e.target.value))} />
                </div>
                <div className="lf-field">
                  <label>Margen de la canasta <b>{pct(marginPct)}</b></label>
                  <input type="range" min="20" max="90" step="0.1" value={marginPct} onChange={(e) => setMarginPct(Number(e.target.value))} />
                  <div className="lf-hint">
                    <button onClick={() => setMarginPct(margenReal)}>usar promedio real ({pct(margenReal)})</button>
                    <span>· {nProductos} productos activos</span>
                  </div>
                </div>
                <div className="lf-field">
                  <label>Ver la plata</label>
                  <div className="lf-toggle">
                    <button className={ivaMode === 'con' ? 'on' : ''} onClick={() => setIvaMode('con')}>Con IVA</button>
                    <button className={ivaMode === 'neto' ? 'on' : ''} onClick={() => setIvaMode('neto')}>Neta de IVA</button>
                  </div>
                </div>
              </div>

              <div className="lf-result">
                <div className={'lf-verdict ' + v.cls}>
                  <div className="lf-big">{r ? fmt(r.contrib) : '—'}</div>
                  <div><div className="lf-vt">{v.t}</div><div className="lf-vs">{v.s}</div></div>
                </div>
                <div className="lf-cards">
                  <div className="lf-rc"><span>Contribución</span><b style={{ color: r && r.contrib > 0 ? '#0e7490' : '#b91c1c' }}>{r ? fmt(r.contrib) : '—'}</b></div>
                  <div className="lf-rc"><span>Ganancia bruta producto</span><b>{r ? fmt(r.gp) : '—'}</b></div>
                  <div className="lf-rc"><span>Envío que absorbés</span><b style={{ color: r && r.free ? '#b91c1c' : '#64748b' }}>{r ? (r.free ? fmt(-r.shipCost) : 'lo paga el cliente') : '—'}</b></div>
                  <div className="lf-rc"><span>Break-even del flete</span><b>{r ? fmt(r.be) : '—'}</b></div>
                </div>
              </div>
            </div>

            {/* ───────── TABLA TODAS LAS ZONAS ───────── */}
            <div className="card" style={{ marginTop: 16, padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', fontWeight: 700, color: '#1A3461', fontSize: 14 }}>
                Todas las zonas al umbral de envío gratis <span style={{ fontWeight: 500, color: '#64748b', fontSize: 12 }}>· margen {pct(marginPct)}</span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="lf-table">
                  <thead><tr>
                    <th>Zona</th><th>Te cuesta</th><th>Cobrás</th><th>Gratis desde</th><th>Break-even</th><th>Contrib. al umbral</th><th>Flete % bruto</th>
                  </tr></thead>
                  <tbody>
                    {calcZonas.map((z) => {
                      const rr = calc(z, z.thr, marginPct, ivaMode)
                      const beOk = z.thr >= rr.be
                      return (
                        <tr key={z.code} className={z.code === sel ? 'hl' : ''}>
                          <td><b>{z.nm}</b></td>
                          <td>{fmt(z.cost)}</td>
                          <td>{fmt(z.charge)}</td>
                          <td>{fmt(z.thr)}</td>
                          <td style={{ color: beOk ? '#059669' : '#d97706', fontWeight: 700 }}>{fmt(rr.be)}</td>
                          <td><b>{fmt(rr.contrib)}</b></td>
                          <td>{pct(rr.flForGP)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ───────── MAPA DE ZONAS ───────── */}
            <h2 style={{ marginTop: 26, fontSize: 16, color: '#1A3461' }}>Mapa de zonas · Montevideo</h2>
            <p className="muted" style={{ marginTop: 2, fontSize: 12.5 }}>
              Pasá el mouse por un barrio para ver su zona.{canEdit ? ' Clic para reasignarlo (se guarda en zona_barrio_geo).' : ''} Canelones/extendida no son barrios INE — no aparecen en el mapa.
            </p>
            <div className="lf-mapwrap">
              <div className="lf-mapcard" onMouseLeave={() => setHover(null)}>
                {geoStatus === 'loading' && <div className="lf-mapmsg">Cargando mapa…</div>}
                {geoStatus === 'error' && <div className="lf-mapmsg">No se pudo cargar el mapa (open data). El resto de la página funciona igual.</div>}
                {geoStatus === 'ok' && mapa && (
                  <svg viewBox={`0 0 ${mapa.W} ${mapa.H}`} className="lf-map"
                    onMouseMove={(e) => {
                      const t = e.target.closest ? e.target.closest('path') : null
                      if (!t || !t.dataset.nm) { setHover(null); return }
                      const rect = e.currentTarget.getBoundingClientRect()
                      const z = zbg[norm(t.dataset.nm)]?.zona || 'sin zona'
                      setHover({ nombre: t.dataset.nm, zona: z, x: e.clientX - rect.left, y: e.clientY - rect.top })
                    }}>
                    {mapa.paths.map((p) => (
                      <path key={p.i} d={p.d} data-nm={p.nombre} className="lf-barrio"
                        fill={ZCOLOR[zbg[norm(p.nombre)]?.zona || 'none']}
                        onClick={(e) => {
                          if (!canEdit) return
                          const rect = e.currentTarget.ownerSVGElement.getBoundingClientRect()
                          setPicker({ key: norm(p.nombre), nombre: p.nombre, x: e.clientX - rect.left, y: e.clientY - rect.top })
                        }} />
                    ))}
                  </svg>
                )}
                {hover && (
                  <div className="lf-tip" style={{ left: hover.x, top: hover.y }}>
                    {hover.nombre} — {ZLABEL[hover.zona] || 'sin zona'}
                  </div>
                )}
                {picker && (
                  <div className="lf-picker" style={{ left: picker.x, top: picker.y }}>
                    <div className="lf-picker-h">{picker.nombre}</div>
                    {ZONA_OPCIONES.map((z) => (
                      <button key={z} disabled={savingZona} onClick={() => reasignarZona(picker.nombre, z)}>
                        <span className="lf-sw" style={{ background: ZCOLOR[z] }} /> {ZLABEL[z]}
                      </button>
                    ))}
                    <button className="lf-picker-x" onClick={() => setPicker(null)}>cerrar</button>
                  </div>
                )}
              </div>
              <div className="lf-mapside">
                <div className="lf-legend">
                  {ZONA_OPCIONES.map((z) => (
                    <div className="lf-lg" key={z}><span className="lf-sw" style={{ background: ZCOLOR[z] }} />{ZLABEL[z]}<b>{mapa ? (mapa.counts[z] || 0) : '—'}</b></div>
                  ))}
                  <div className="lf-lg"><span className="lf-sw" style={{ background: ZCOLOR.none }} />Sin zona<b>{mapa ? (mapa.counts.none || 0) : '—'}</b></div>
                </div>
                {mapa && (
                  <div className="lf-diag">
                    <b>{mapa.matched}</b> de {mapa.total} barrios matchearon (prop. <code>{mapa.nameKey}</code>).
                    {mapa.unmatched.length > 0 && <div className="lf-unmatched"><b>Sin zona:</b> {mapa.unmatched.join(' · ')}{canEdit ? ' — clickealos en el mapa para asignarlos.' : ''}</div>}
                  </div>
                )}
              </div>
            </div>

            {/* ───────── EDITOR ZONAS ───────── */}
            <h2 style={{ marginTop: 26, fontSize: 16, color: '#1A3461' }}>Costos y tarifas por zona</h2>
            <p className="muted" style={{ marginTop: 2, fontSize: 12.5 }}>
              <code>costo_nahuel</code> = lo que te cuesta el cadete. <code>precio_flex</code> = lo que cobrás. Umbral vacío usa el global de Montevideo.
            </p>
            <div className="lf-grid">
              {zonas.map((z) => {
                const dirty = zonaDirty(z)
                const margenEnvio = z.precio_flex - z.costo_nahuel
                return (
                  <div key={z.id} className="lf-ecard" style={{ borderLeft: `4px solid ${z.color_hex || '#1A3461'}`, borderColor: dirty ? '#3B82F6' : '#E2E8F0' }}>
                    <div className="lf-ecard-h">
                      <strong>{z.nombre}</strong>
                      <span className="lf-margin-badge" style={{ color: margenEnvio >= 0 ? '#059669' : '#b91c1c' }}>envío {fmt(margenEnvio)}</span>
                    </div>
                    <div className="lf-inputs">
                      <label>Costo Nahuel<div><span>$</span><input type="number" disabled={!canEdit} value={z.costo_nahuel} onChange={(e) => setZona(z.id, 'costo_nahuel', Number(e.target.value) || 0)} /></div></label>
                      <label>Precio Flex<div><span>$</span><input type="number" disabled={!canEdit} value={z.precio_flex} onChange={(e) => setZona(z.id, 'precio_flex', Number(e.target.value) || 0)} /></div></label>
                      <label>Umbral gratis<div><span>$</span><input type="number" disabled={!canEdit} placeholder="global" value={z.umbral_gratis ?? ''} onChange={(e) => setZona(z.id, 'umbral_gratis', e.target.value === '' ? null : Number(e.target.value))} /></div></label>
                    </div>
                    <div className="lf-clave">{z.codigo}{dirty && <span className="lf-dot">• modificado</span>}</div>
                  </div>
                )
              })}
            </div>

            {/* ───────── EDITOR PARÁMETROS ONESHOT ───────── */}
            <h2 style={{ marginTop: 26, fontSize: 16, color: '#1A3461' }}>Parámetros OneShot</h2>
            <div className="lf-grid">
              {PARAM_META.map((p) => {
                const dirty = Number(params[p.clave]) !== Number(paramsOrig[p.clave])
                return (
                  <div key={p.clave} className="lf-ecard" style={{ borderLeft: `4px solid ${p.color}`, borderColor: dirty ? '#3B82F6' : '#E2E8F0' }}>
                    <strong style={{ fontSize: 13, color: '#1A3461' }}>{p.titulo}</strong>
                    <p style={{ margin: '4px 0 10px', fontSize: 11.5, color: '#64748B', lineHeight: 1.5 }}>{p.desc}</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 15, color: '#475569' }}>{p.unidad}</span>
                      <input type="number" disabled={!canEdit} value={params[p.clave] ?? 0} onChange={(e) => setParam(p.clave, Number(e.target.value) || 0)}
                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 17, fontWeight: 800, color: '#1A3461', background: canEdit ? '#fff' : '#f8fafc' }} />
                    </div>
                    <div className="lf-clave">{p.clave}{dirty && <span className="lf-dot">• era {paramsOrig[p.clave]}</span>}</div>
                  </div>
                )
              })}
            </div>

            <div style={{ marginTop: 22, padding: 14, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10, fontSize: 12, color: '#78350f', lineHeight: 1.6 }}>
              <strong>📌 Notas.</strong> Margen 52,9% es con IVA (convención de la casa); en «neta de IVA» se divide por 1,22 y el flete va a costo pleno (cadete sin factura). El cobro real al cliente lo resuelve la RPC <code>calcular_envio_oneshot</code> en el backend; esto es gestión + preview. Zona extendida y privados comparten umbral gratis ($2.000).
            </div>
          </>
        )}
      </div>

      {/* Barra de guardado */}
      {canEdit && hayCambios && (
        <div className="lf-savebar">
          <span>{zonasDirty.length + paramsDirty.length} cambio(s) sin guardar</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="lf-btn-ghost" onClick={descartar} disabled={saving}>Descartar</button>
            <button className="lf-btn-save" onClick={guardar} disabled={saving}>{saving ? 'Guardando…' : 'Guardar cambios'}</button>
          </div>
        </div>
      )}

      <style>{`
        .lf-admin code { background:#f1f5f9; padding:1px 5px; border-radius:4px; font-size:11.5px; color:#475569; }
        .lf-calc { display:grid; gap:16px; grid-template-columns:1fr; }
        @media(min-width:900px){ .lf-calc { grid-template-columns:minmax(320px,380px) 1fr; } }
        .lf-controls, .lf-result { background:#fff; border:1px solid #E2E8F0; border-radius:14px; padding:18px; }
        .lf-field { margin-bottom:16px; } .lf-field:last-child { margin-bottom:0; }
        .lf-field > label { display:block; font-size:12.5px; font-weight:700; color:#334155; margin-bottom:8px; }
        .lf-field > label b { float:right; color:#00A8B5; font-size:15px; }
        .lf-field input[type=range] { width:100%; accent-color:#00A8B5; }
        .lf-hint { margin-top:6px; font-size:11px; color:#94a3b8; }
        .lf-hint button { background:none; border:none; color:#00A8B5; font-weight:700; cursor:pointer; padding:0; font-size:11px; text-decoration:underline; }
        .lf-zbtns { display:grid; grid-template-columns:1fr 1fr; gap:7px; }
        .lf-zbtn { text-align:left; background:#f8fafc; border:1px solid #E2E8F0; border-radius:10px; padding:8px 10px; cursor:pointer; }
        .lf-zbtn b { display:block; font-size:12px; color:#1A3461; } .lf-zbtn span { font-size:10px; color:#94a3b8; }
        .lf-zbtn.on { background:#ecfeff; border-color:#00A8B5; box-shadow:0 0 0 1px #00A8B5 inset; }
        .lf-verdict { display:flex; align-items:center; gap:14px; border-radius:12px; padding:16px; margin-bottom:14px; border:1px solid; }
        .lf-big { font-size:30px; font-weight:900; letter-spacing:-0.02em; }
        .lf-vt { font-size:12px; font-weight:800; text-transform:uppercase; letter-spacing:.04em; } .lf-vs { font-size:12px; color:#475569; margin-top:2px; }
        .v-ok { background:#ecfdf5; border-color:#a7f3d0; } .v-ok .lf-big,.v-ok .lf-vt { color:#059669; }
        .v-warn { background:#fffbeb; border-color:#fde68a; } .v-warn .lf-big,.v-warn .lf-vt { color:#d97706; }
        .v-bad { background:#fef2f2; border-color:#fecaca; } .v-bad .lf-big,.v-bad .lf-vt { color:#dc2626; }
        .lf-cards { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
        .lf-rc { background:#f8fafc; border:1px solid #eef2f7; border-radius:10px; padding:12px; }
        .lf-rc span { display:block; font-size:11px; color:#64748b; margin-bottom:4px; } .lf-rc b { font-size:19px; font-weight:900; color:#1A3461; }
        .lf-table { width:100%; border-collapse:collapse; font-size:12.5px; }
        .lf-table th,.lf-table td { padding:9px 12px; text-align:right; border-bottom:1px solid #eef2f7; font-variant-numeric:tabular-nums; }
        .lf-table th:first-child,.lf-table td:first-child { text-align:left; }
        .lf-table th { font-size:10px; text-transform:uppercase; letter-spacing:.04em; color:#94a3b8; background:#f8fafc; }
        .lf-table tr.hl td { background:#ecfeff; }
        .lf-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(260px,1fr)); gap:12px; margin-top:12px; }
        .lf-ecard { background:#fff; border:1px solid #E2E8F0; border-radius:10px; padding:14px; }
        .lf-ecard-h { display:flex; justify-content:space-between; align-items:center; margin-bottom:10px; }
        .lf-ecard-h strong { font-size:13px; color:#1A3461; }
        .lf-margin-badge { font-size:11px; font-weight:800; }
        .lf-inputs { display:grid; grid-template-columns:1fr 1fr 1fr; gap:8px; }
        .lf-inputs label { font-size:10.5px; font-weight:700; color:#64748b; }
        .lf-inputs label > div { display:flex; align-items:center; gap:3px; margin-top:3px; }
        .lf-inputs span { font-size:12px; color:#94a3b8; }
        .lf-inputs input { width:100%; padding:6px 7px; border:1px solid #CBD5E1; border-radius:6px; font-size:14px; font-weight:800; color:#1A3461; }
        .lf-inputs input:disabled { background:#f8fafc; color:#94a3b8; }
        .lf-clave { margin-top:8px; font-size:10px; color:#94a3b8; font-family:monospace; }
        .lf-dot { color:#3B82F6; font-weight:700; margin-left:8px; }
        .lf-savebar { position:sticky; bottom:0; left:0; right:0; background:#1A3461; color:#fff; padding:12px 20px; display:flex; justify-content:space-between; align-items:center; box-shadow:0 -4px 16px rgba(0,0,0,0.15); z-index:40; font-size:13px; font-weight:600; border-radius:12px 12px 0 0; margin-top:20px; }
        .lf-btn-ghost { background:rgba(255,255,255,0.12); color:#fff; border:1px solid rgba(255,255,255,0.25); padding:8px 16px; border-radius:8px; font-weight:700; font-size:13px; cursor:pointer; }
        .lf-btn-save { background:#00A8B5; color:#fff; border:none; padding:8px 18px; border-radius:8px; font-weight:800; font-size:13px; cursor:pointer; }
        .lf-btn-save:disabled { opacity:.6; cursor:not-allowed; }
        .lf-mapwrap { display:grid; gap:14px; grid-template-columns:1fr; margin-top:12px; }
        @media(min-width:900px){ .lf-mapwrap { grid-template-columns:1fr 280px; } }
        .lf-mapcard { position:relative; background:#fff; border:1px solid #E2E8F0; border-radius:14px; padding:12px; min-height:280px; display:flex; align-items:center; justify-content:center; }
        .lf-map { width:100%; height:auto; display:block; }
        .lf-barrio { stroke:#fff; stroke-width:0.6; cursor:pointer; transition:opacity .1s; }
        .lf-barrio:hover { opacity:.75; }
        .lf-mapmsg { color:#94a3b8; font-size:13px; padding:40px; text-align:center; }
        .lf-tip { position:absolute; pointer-events:none; transform:translate(-50%,-130%); background:#1A3461; color:#fff; font-size:12px; font-weight:600; padding:5px 9px; border-radius:7px; white-space:nowrap; z-index:6; }
        .lf-picker { position:absolute; transform:translate(-50%,8px); background:#fff; border:1px solid #E2E8F0; border-radius:10px; box-shadow:0 8px 24px rgba(15,23,42,.18); padding:8px; z-index:7; min-width:180px; }
        .lf-picker-h { font-size:11px; font-weight:800; color:#1A3461; padding:2px 6px 8px; }
        .lf-picker button { display:flex; align-items:center; gap:8px; width:100%; text-align:left; background:none; border:none; padding:7px 6px; border-radius:6px; font-size:12.5px; color:#334155; cursor:pointer; font-family:inherit; }
        .lf-picker button:hover:not(:disabled) { background:#f1f5f9; }
        .lf-picker-x { color:#94a3b8 !important; font-size:11px !important; justify-content:center !important; }
        .lf-sw { width:13px; height:13px; border-radius:4px; border:1px solid rgba(0,0,0,.12); display:inline-block; flex-shrink:0; }
        .lf-mapside { background:#fff; border:1px solid #E2E8F0; border-radius:14px; padding:14px; }
        .lf-legend { display:flex; flex-direction:column; gap:7px; margin-bottom:12px; }
        .lf-lg { display:flex; align-items:center; gap:8px; font-size:13px; color:#334155; }
        .lf-lg b { margin-left:auto; font-variant-numeric:tabular-nums; color:#1A3461; }
        .lf-diag { font-size:12px; color:#475569; background:#f8fafc; border:1px solid #eef2f7; border-radius:9px; padding:10px; line-height:1.5; }
        .lf-unmatched { color:#b45309; font-size:11px; margin-top:6px; word-break:break-word; }
      `}</style>
    </AdminLayout>
  )
}
